/**
 * The Activity feed and the sidebar Activity badge (logic/activity): my items with unread state, the filter
 * (All / Mentions / Threads / Reactions), opening an item (marks it read) and "Mark all as read". A realtime
 * `notification` (an @-mention, a reply in a thread I follow) updates the items and the badge at once
 * (logic/activity `applyNotification`); the list is then refetched (debounced) to take the server's version, as it is
 * for the other hints — a reaction or thread reply by someone else, being added to a channel — and when the app comes
 * back to the foreground. There is no dedicated realtime event (older clients' event decoders are strict).
 */
import type { ActivityItem, NotificationEvent, PersistentEvent } from "@bundle/contracts";
import { type ActivityFilter, applyNotification, markRead, unreadCount } from "../logic/activity.ts";
import type { Clock, SettingsApi } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

export type ActivityState = {
  /** "loading" until the first answer; later refreshes keep the list on screen. */
  status: "loading" | "ready" | "failed";
  items: ActivityItem[];
  filter: ActivityFilter;
  /** Unread items (the badge). */
  unread: number;
};

export type ActivityDeps = {
  api: Pick<SettingsApi, "activity" | "markActivityRead">;
  clock: Clock;
};

/** Realtime hints coalesce into one refresh after this long. */
export const ACTIVITY_REFRESH_DEBOUNCE_MS = 1500;

export class ActivityViewModel extends ViewModel<ActivityState> {
  private cancelTimer: (() => void) | undefined;
  private inFlight: Promise<void> | undefined;
  private again = false;

  constructor(
    private readonly deps: ActivityDeps,
    private readonly ws: string,
    private readonly meId: string,
  ) {
    super({ status: "loading", items: [], filter: "all", unread: 0 });
    this.onDispose(() => this.cancelTimer?.());
  }

  /** Fetches the feed now (one request at a time; a call during one runs once more after it). */
  refresh(): Promise<void> {
    if (this.inFlight !== undefined) {
      this.again = true;
      return this.inFlight;
    }
    this.inFlight = this.deps.api.activity(this.ws).then(
      (items) => this.update({ status: "ready", items, unread: unreadCount(items) }),
      () => {
        if (this.state.status === "loading") this.update({ status: "failed" });
      },
    );
    return this.inFlight.finally(() => {
      this.inFlight = undefined;
      if (this.again) {
        this.again = false;
        void this.refresh();
      }
    });
  }

  /** "Try again" after a failed first load. */
  retry(): void {
    this.update({ status: "loading" });
    void this.refresh();
  }

  /** A refresh shortly (hints arrive in bursts). */
  refreshSoon(): void {
    this.cancelTimer?.();
    this.cancelTimer = this.deps.clock.setTimer(ACTIVITY_REFRESH_DEBOUNCE_MS, () => {
      this.cancelTimer = undefined;
      void this.refresh();
    });
  }

  /** A realtime `notification` arrived (mention, followed-thread reply, huddle ring): the badge moves now. */
  onNotification(event: NotificationEvent): void {
    if (this.state.status === "ready") {
      const items = applyNotification(this.state.items, event, this.meId);
      if (items.length !== this.state.items.length || items.some((it, i) => it !== this.state.items[i])) this.apply(items);
    }
    this.refreshSoon();
  }

  /** A workspace event: refresh for the ones that may have added an item of mine. */
  onEvent(event: PersistentEvent): void {
    if (wantsRefresh(event, this.meId)) this.refreshSoon();
  }

  setFilter(filter: ActivityFilter): void {
    this.update({ filter });
  }

  /** Opening an item marks it read (here at once; the server after). */
  open(id: string): void {
    const item = this.state.items.find((it) => it.id === id);
    if (item?.unread !== true) return;
    this.apply(markRead(this.state.items, [id]));
    this.deps.api.markActivityRead(this.ws, [id]).catch(() => undefined);
  }

  markAllRead(): void {
    if (this.state.unread === 0) return;
    this.apply(markRead(this.state.items));
    this.deps.api.markActivityRead(this.ws).catch(() => this.refreshSoon());
  }

  private apply(items: ActivityItem[]): void {
    this.update({ items, unread: unreadCount(items) });
  }
}

/** Events that may have added an Activity item for me: others' reactions and thread replies, being added to a channel. */
export function wantsRefresh(event: PersistentEvent, meId: string): boolean {
  switch (event.type) {
    case "reaction.added":
    case "reaction.removed":
      return event.userId !== meId;
    case "message.created":
      return event.message.threadRootId !== undefined && event.message.authorId !== meId;
    case "channel.member_joined":
      return event.userId === meId;
    default:
      return false;
  }
}
